/**
 * MembersTable - Tabela de membros da organização
 * Refatorado para usar DataTable genérico e ActionsDropdown
 */
import React, { useMemo } from 'react';
import { MailIcon } from '../../../components/icons';
import { formatDateBR } from '../../../services/utils/serviceUtils';
import DataTable, { Column } from '../../../components/shared/DataTable';
import ActionMenu, { ActionMenuItem } from '../../../components/shared/ActionMenu';

export interface MemberData {
    id: string;
    user_id: string;
    role: 'owner' | 'admin' | 'member' | 'viewer';
    joined_at: string;
    user?: {
        full_name?: string;
        email?: string;
    };
}

interface MembersTableProps {
    members: MemberData[];
    isLoading: boolean;
    searchTerm: string;
    currentUserRole: 'owner' | 'admin' | 'member' | 'viewer';
    currentUserId: string;
    onEdit: (member: MemberData) => void;
    onResetPassword: (member: MemberData) => void;
    onDelete: (member: MemberData) => void;
}

const getRoleBadgeClass = (role: string) => {
    switch (role) {
        case 'owner': return 'bg-primary-subtle text-primary';
        case 'admin': return 'bg-highlight-subtle text-highlight-dark';
        case 'member': return 'bg-accent-subtle text-accent-dark';
        default: return 'bg-surface-subtle text-secondary border border-border';
    }
};

const getRoleLabel = (role: string) => {
    switch (role) {
        case 'owner': return 'Dono';
        case 'admin': return 'Admin';
        case 'member': return 'Membro';
        default: return 'Visualizador';
    }
};

const MembersTable: React.FC<MembersTableProps> = ({
    members,
    isLoading,
    searchTerm,
    currentUserRole,
    currentUserId,
    onEdit,
    onResetPassword,
    onDelete
}) => {
    const columns: Column<MemberData>[] = useMemo(() => [
        {
            key: 'user',
            header: 'Usuário',
            render: (member) => <span className="font-medium text-foreground">{member.user?.full_name || 'Sem nome'}</span>
        },
        {
            key: 'email',
            header: 'Email',
            render: (member) => (
                <div className="flex items-center text-muted-foreground">
                    <MailIcon className="w-4 h-4 mr-2 text-muted-foreground" />
                    {member.user?.email}
                </div>
            )
        },
        {
            key: 'role',
            header: 'Função',
            render: (member) => (
                <span className={`px-2 py-1 rounded-full text-xs font-semibold ${getRoleBadgeClass(member.role)}`}>
                    {getRoleLabel(member.role)}
                </span>
            )
        },
        {
            key: 'joined_at',
            header: 'Entrou em',
            render: (member) => <span className="text-muted-foreground">{formatDateBR(member.joined_at)}</span>
        },
        {
            key: 'actions',
            header: 'Ações',
            render: (member) => {
                // Owner não pode ser editado ou excluído
                if (member.role === 'owner') return null;

                // Só owner pode editar/excluir membros
                // Admin pode resetar senha
                const isOwner = currentUserRole === 'owner';
                const isAdminOrOwner = currentUserRole === 'owner' || currentUserRole === 'admin';
                const isSelf = member.user_id === currentUserId;

                const actions: ActionMenuItem[] = [];

                // Editar (apenas owner)
                if (isOwner && !isSelf) {
                    actions.push({
                        label: 'Editar',
                        icon: 'edit',
                        onClick: () => onEdit(member)
                    });
                }

                // Redefinir senha (owner ou admin)
                if (isAdminOrOwner && !isSelf) {
                    actions.push({
                        label: 'Redefinir senha',
                        icon: 'key',
                        onClick: () => onResetPassword(member)
                    });
                }

                // Excluir (apenas owner)
                if (isOwner && !isSelf) {
                    actions.push({
                        label: 'Excluir',
                        icon: 'trash',
                        onClick: () => onDelete(member),
                        variant: 'danger'
                    });
                }

                if (actions.length === 0) return null;

                return <ActionMenu actions={actions} />;
            }
        }
    ], [currentUserRole, currentUserId, onEdit, onResetPassword, onDelete]);

    if (isLoading) {
        return <div className="p-8 text-center text-muted-foreground">Carregando membros...</div>;
    }

    return (
        <DataTable
            columns={columns}
            data={members}
            getRowKey={(m) => m.id}
            emptyMessage={searchTerm ? 'Nenhum membro encontrado para a busca.' : 'Nenhum membro encontrado.'}
        />
    );
};

export default MembersTable;
